// src/components/admin/forms/ToolForm.js
import React, { useState, useContext, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { DashboardContext } from "../../../contexts/AdminContext";
import api from "../../../axiosConfig";
import FormNotification, {
  useFormNotification,
} from "../../common/FormNotification";

const ToolForm = ({ show, handleClose, initialData }) => {
  const { addTool, updateTool, sectors } = useContext(DashboardContext);

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [spec, setSpec] = useState("");
  const [category, setCategory] = useState("");
  const [sector, setSector] = useState("");
  const [photo, setPhoto] = useState(""); // base64 data URL, "" = tanpa foto
  const [photoLoading, setPhotoLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { notif, notifySuccess, notifyError, clearNotif } =
    useFormNotification();

  const categories = [
    "Dies",
    "Jig",
    "Nozel",
    "Moulding",
    "Pin",
    "Hole",
    "Brading",
    "Pemilin",
  ];

  // Daftar sektor diambil dari master sektor (context) supaya selalu
  // sinkron dengan halaman Sektor. Jika sektor lama pada tool ini tidak
  // ada di master, tetap dimasukkan agar nilainya tidak hilang saat edit.
  const sectorOptions = React.useMemo(() => {
    const names = (sectors || [])
      .map((s) => s.name?.trim())
      .filter(Boolean);
    const current = initialData?.sector_name?.trim();
    if (current && !names.includes(current)) names.push(current);
    return names.sort((a, b) => a.localeCompare(b, "id", { numeric: true }));
  }, [sectors, initialData]);

  // 🔹 Set default value ketika modal dibuka
  useEffect(() => {
    clearNotif();
    setLoading(false);
    if (initialData) {
      setName(initialData.part_name || "");
      setCode(initialData.kode || "");
      setSpec(initialData.spec || "");
      setCategory(initialData.category || "");
      setSector(initialData.sector_name || "");
      // Ambil foto tool yang tersimpan (dipisah dari list agar list tidak berat)
      setPhoto("");
      if (initialData.id) {
        setPhotoLoading(true);
        api
          .get(`/tools/${initialData.id}/photo`)
          .then((res) => setPhoto(res.data?.photo || ""))
          .catch((err) => console.error("Gagal ambil foto tool:", err))
          .finally(() => setPhotoLoading(false));
      }
    } else {
      setName("");
      setCode("");
      setSpec("");
      setCategory("");
      setSector("");
      setPhoto("");
    }
  }, [initialData, show]);

  // Upload foto: kompres (maks 1000px, JPEG 0.8) -> base64
  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1000;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setPhoto(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearNotif();

    if (!name || !code || !category || !sector) {
      notifyError("Part Name, Kode, Kategori, dan Sector wajib diisi.");
      return;
    }

    const data = {
      part_name: name,
      kode: code,
      spec,
      category,
      sector_name: sector,
      photo, // "" = hapus foto, base64 = simpan foto
    };

    setLoading(true);
    try {
      const result = initialData
        ? await updateTool(initialData.id, data)
        : await addTool(data);

      if (result && result.success === false) {
        notifyError(result.error || "Gagal menyimpan tool");
        setLoading(false);
        return;
      }

      notifySuccess(
        initialData
          ? "Data tool berhasil diperbarui."
          : "Tool baru berhasil ditambahkan."
      );
      // Banner sukses tampil sebentar sebelum modal ditutup.
      setTimeout(() => handleClose(), 1300);
    } catch (err) {
      console.error("Gagal menyimpan tool:", err);
      notifyError(err.message || "Terjadi kesalahan saat menyimpan tool");
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{initialData ? "Edit Tool" : "Tambah Tool"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormNotification notif={notif} onClose={clearNotif} />
          <Form.Group className="mb-2">
            <Form.Label>Part Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Kode</Form.Label>
            <Form.Control
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Spec</Form.Label>
            <Form.Control
              type="text"
              value={spec}
              onChange={(e) => setSpec(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Kategori</Form.Label>
            <Form.Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="">-- Pilih Kategori --</option>
              {categories.map((cat, idx) => (
                <option key={idx} value={cat}>
                  {cat}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Sector</Form.Label>
            <Form.Select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              required
            >
              <option value="">-- Pilih Sector --</option>
              {sectorOptions.map((sec, idx) => (
                <option key={idx} value={sec}>
                  {sec}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Foto Tool (untuk Sketch di print)</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handlePhoto}
            />
            {photoLoading && (
              <div className="text-muted mt-1" style={{ fontSize: "12px" }}>
                Memuat foto...
              </div>
            )}
            {photo && (
              <div className="mt-2 text-center">
                <img
                  src={photo}
                  alt="Foto tool"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "220px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                  }}
                />
                <div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="mt-2"
                    onClick={() => setPhoto("")}
                  >
                    Hapus Foto
                  </Button>
                </div>
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Batal
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? "Menyimpan..." : initialData ? "Simpan" : "Tambah"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ToolForm;
